/**
 * Breadcrumb Navigation Data
 * 
 * Pre-configured breadcrumb trails for all pages.
 * Use with getBreadcrumbLD() for structured data.
 * 
 * Breadcrumbs improve CTR in SERPs by showing page hierarchy.
 */

import { PUBLIC_SITE_URL } from './seo';

// ============================================================================
// HELPER FUNCTION
// ============================================================================

/**
 * Create breadcrumb trail
 * @param {...object} crumbs - Breadcrumb objects with name and path
 * @returns {Array} Full breadcrumb trail with complete URLs
 */
const createTrail = (...crumbs) => {
  return [
    { name: 'Home', url: PUBLIC_SITE_URL },
    ...crumbs.map(crumb => ({
      name: crumb.name,
      url: `${PUBLIC_SITE_URL}${crumb.path}`
    }))
  ];
};

// ============================================================================
// SERVICE PAGE BREADCRUMBS
// ============================================================================

export const invisalignBreadcrumbs = createTrail(
  { name: 'Services', path: '/services' }, // Logical parent (even if page doesn't exist yet)
  { name: 'Invisalign Acton', path: '/invisalign-acton' }
);

export const implantsBreadcrumbs = createTrail(
  { name: 'Services', path: '/services' },
  { name: 'Dental Implants Acton', path: '/implants-acton' }
);

export const compositeBondingBreadcrumbs = createTrail(
  { name: 'Services', path: '/services' },
  { name: 'Composite Bonding Acton', path: '/composite-bonding-acton' }
);

export const cosmeticDentistBreadcrumbs = createTrail(
  { name: 'Services', path: '/services' },
  { name: 'Cosmetic Dentist Acton', path: '/cosmetic-dentist-acton' }
);

export const emergencyDentistBreadcrumbs = createTrail(
  { name: 'Services', path: '/services' },
  { name: 'Emergency Dentist Acton', path: '/emergency-dentist-acton' }
);

export const dentistActonBreadcrumbs = createTrail(
  { name: 'Services', path: '/services' },
  { name: 'General Dentist Acton', path: '/dentist-acton' }
);

// ============================================================================
// FINANCE PAGE BREADCRUMBS
// ============================================================================

export const financeBreadcrumbs = createTrail(
  { name: 'Finance Options', path: '/finance' }
);

// ============================================================================
// FUTURE: KNOWLEDGE BASE BREADCRUMBS
// ============================================================================

/**
 * Generate breadcrumbs for KB articles
 * @param {string} articleSlug - Article URL slug
 * @param {string} articleTitle - Article title
 * @returns {Array} Breadcrumb trail
 */
export const getKBArticleBreadcrumbs = (articleSlug, articleTitle) => {
  return createTrail(
    { name: 'Knowledge Base', path: '/knowledge-base' },
    { name: articleTitle, path: `/knowledge-base/${articleSlug}` }
  );
};

// Example:
// const breadcrumbs = getKBArticleBreadcrumbs(
//   'why-do-my-veneers-look-fake',
//   'Why Do My Veneers Look Fake?'
// );

// ============================================================================
// FUTURE: AREA/LOCATION BREADCRUMBS
// ============================================================================

/**
 * Generate breadcrumbs for area/location pages
 * @param {string} areaSlug - Area URL slug
 * @param {string} areaName - Area display name
 * @returns {Array} Breadcrumb trail
 */
export const getAreaBreadcrumbs = (areaSlug, areaName) => {
  return createTrail(
    { name: 'Areas We Serve', path: '/areas' },
    { name: areaName, path: `/areas/${areaSlug}` }
  );
};

// Example:
// const breadcrumbs = getAreaBreadcrumbs(
//   'fitzrovia',
//   'TMD Dentist Fitzrovia'
// );

// ============================================================================
// FUTURE: ABOUT/INFO PAGES BREADCRUMBS
// ============================================================================

export const aboutBreadcrumbs = createTrail(
  { name: 'About Us', path: '/about' }
);

export const contactBreadcrumbs = createTrail(
  { name: 'Contact', path: '/contact' }
);

export const feesBreadcrumbs = createTrail(
  { name: 'Fees & Pricing', path: '/fees' }
);

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Service Pages
  invisalignBreadcrumbs,
  implantsBreadcrumbs,
  compositeBondingBreadcrumbs,
  cosmeticDentistBreadcrumbs,
  emergencyDentistBreadcrumbs,
  dentistActonBreadcrumbs,
  
  // Finance
  financeBreadcrumbs,
  
  // Info Pages
  aboutBreadcrumbs,
  contactBreadcrumbs,
  feesBreadcrumbs,
  
  // Dynamic Generators
  getKBArticleBreadcrumbs,
  getAreaBreadcrumbs,
};
